import { createElement, type ReactNode } from 'react';
import { iconSizeClasses, IconSizes } from './icon-sizes';
import { type IconNode, type IconProps, type NewcoreIcon } from './types';
import { cn } from './utils/cn';

const DEFAULT_SIZE = IconSizes.SM;

function toPascalCase(name: string) {
  return name
    .split('-')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join('');
}

function renderNode(node: IconNode): ReactNode[] {
  return node.map(([tag, attrs, children], index) =>
    createElement(
      tag,
      { ...attrs, key: index },
      children ? renderNode(children) : undefined
    )
  );
}

function hasAriaLabel(props: IconProps) {
  return 'aria-label' in props || 'aria-labelledby' in props;
}

export function createIcon(
  name: string,
  rootAttrs: Record<string, string>,
  node: IconNode
): NewcoreIcon {
  function Icon({ size = DEFAULT_SIZE, className, ...props }: IconProps) {
    const isLabelled = hasAriaLabel(props);

    return (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        data-icon={name}
        aria-hidden={isLabelled ? undefined : true}
        {...rootAttrs}
        {...props}
        className={cn(iconSizeClasses[size], className)}
      >
        {renderNode(node)}
      </svg>
    );
  }

  Icon.displayName = `${toPascalCase(name)}Icon`;
  Icon.iconName = name;

  return Icon;
}
